import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Eye,
  EyeOff,
  Upload,
  Building2,
  FileText,
  Shield,
  CheckCircle,
  Mail,
  Clock,
  CreditCard,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { authAPI } from "../../services/api";
import LoadingSpinner from "../common/LoadingSpinner";
import { toast } from "sonner";

const KYBForm = ({ onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [stepError, setStepError] = useState("");

  // Bank Account Information states
  const [bankAccountTypes, setBankAccountTypes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState({
    conventional: {
      bankName: "",
      accountName: "",
      accountNumber: "",
      accountCategory: "",
      swiftBic: "",
      branchCode: "",
    },
    islamic: {
      bankName: "",
      accountName: "",
      accountNumber: "",
      accountCategory: "",
      swiftBic: "",
      branchCode: "",
      shariahComplianceFile: null,
    },
  });

  // OTP verification states
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(true);

  const { register: registerUser, isRegistering } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  const steps = [
    { id: 1, title: "Role Selection", icon: Shield },
    { id: 2, title: "Account Creation", icon: Building2 },
    { id: 3, title: "Bank Account Information", icon: CreditCard },
    { id: 4, title: "Corporate Information", icon: FileText },
  ];

  const corporateRoles = [
    {
      value: "investor",
      label: "Investor (Direct)",
      description: "Direct institutional investor",
    },
    {
      value: "custodian",
      label: "Custodian",
      description: "Asset custody services",
    },
  ];

  const bankOptions = [
    "Affin Bank",
    "Agro Bank",
    "Alliance Bank",
    "Am Bank",
    "Bank Islam",
    "Bank Muamalat",
    "Bank Rakyat",
    "Bank Simpanan National",
    "CIMB Bank",
    "Hong Leong Bank",
    "HSBC Bank",
    "Kuwait Finance House",
    "Maybank",
    "OCBC Bank",
    "Public Bank",
    "RHB Bank",
    "Standard Chartered Bank",
    "UOB Bank",
  ];

  // Bank Account helper functions
  const handleBankAccountTypeChange = (accountType, isChecked) => {
    if (isChecked) {
      setBankAccountTypes((prev) => [...prev, accountType]);
    } else {
      setBankAccountTypes((prev) =>
        prev.filter((type) => type !== accountType)
      );
      // Clear the account data when unchecking
      setBankAccounts((prev) => ({
        ...prev,
        [accountType]: {
          bankName: "",
          accountName: "",
          accountNumber: "",
          accountCategory: "",
          swiftBic: "",
          branchCode: "",
          ...(accountType === "islamic" && { shariahComplianceFile: null }),
        },
      }));
    }
  };

  const updateBankAccount = (accountType, field, value) => {
    setBankAccounts((prev) => ({
      ...prev,
      [accountType]: {
        ...prev[accountType],
        [field]: value,
      },
    }));
  };

  const handleShariahComplianceUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setBankAccounts((prev) => ({
        ...prev,
        islamic: {
          ...prev.islamic,
          shariahComplianceFile: file,
        },
      }));
      setUploadedFiles((prev) => ({
        ...prev,
        islamic_shariahCompliance: file,
      }));
    }
  };

  // OTP handling functions
  const sendOTP = async () => {
    const email = watch("email");
    if (!email) {
      toast.error("Please enter your email address first");
      return;
    }

    setOtpLoading(true);
    try {
      await authAPI.sendOTP(email);
      setOtpSent(true);
      setCanResendOtp(false);
      setOtpCountdown(60); // 60 seconds cooldown
      toast.success("OTP sent to your email address");

      // Start countdown timer
      const timer = setInterval(() => {
        setOtpCountdown((prev) => {
          if (prev <= 1) {
            setCanResendOtp(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      const message = error.response?.data?.message || "Failed to send OTP";
      toast.error(message);
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOTP = async () => {
    const email = watch("email");
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setOtpLoading(true);
    try {
      await authAPI.verifyOTP(email, otpCode);
      setOtpVerified(true);
      toast.success("Email verified successfully!");
    } catch (error) {
      const message = error.response?.data?.message || "Invalid OTP";
      toast.error(message);
      setOtpCode(""); // Clear invalid OTP
    } finally {
      setOtpLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      // Prepare data for submission
      const formData = new FormData();

      // Basic account info
      formData.append("email", data.email);
      formData.append("password", data.password);
      formData.append("accountType", "corporate");
      formData.append("role", data.corporateRole);
      formData.append("lei", data.lei.trim().toUpperCase()); // Add LEI

      // Bank Account Information
      formData.append("bankAccountTypes", JSON.stringify(bankAccountTypes));
      bankAccountTypes.forEach((accountType) => {
        const account = bankAccounts[accountType];
        formData.append(`${accountType}_bankName`, account.bankName);
        formData.append(`${accountType}_accountName`, account.accountName);
        formData.append(`${accountType}_accountNumber`, account.accountNumber);
        formData.append(
          `${accountType}_accountCategory`,
          account.accountCategory
        );
        if (account.swiftBic)
          formData.append(`${accountType}_swiftBic`, account.swiftBic);
        if (account.branchCode)
          formData.append(`${accountType}_branchCode`, account.branchCode);
        if (
          accountType === "islamic" &&
          uploadedFiles[`${accountType}_shariahCompliance`]
        ) {
          formData.append(
            `${accountType}_shariahCompliance`,
            uploadedFiles[`${accountType}_shariahCompliance`]
          );
        }
      });

      // Corporate information - only append if fields have values
      if (data.legalEntityName)
        formData.append("legalEntityName", data.legalEntityName);
      if (data.businessRegistrationNumber)
        formData.append(
          "businessRegistrationNumber",
          data.businessRegistrationNumber
        );
      if (data.registrationType)
        formData.append("registrationType", data.registrationType);
      if (data.dateOfIncorporation)
        formData.append("dateOfIncorporation", data.dateOfIncorporation);
      if (data.registeredAddress)
        formData.append("registeredAddress", data.registeredAddress);
      if (data.operatingAddress)
        formData.append("operatingAddress", data.operatingAddress);
      if (data.phoneNumber) formData.append("phoneNumber", data.phoneNumber);
      if (data.website) formData.append("website", data.website);
      if (data.businessNature)
        formData.append("businessNature", data.businessNature);
      if (data.annualRevenue)
        formData.append("annualRevenue", data.annualRevenue);
      if (data.numberOfEmployees)
        formData.append("numberOfEmployees", data.numberOfEmployees);

      await registerUser(formData);
    } catch (error) {
      console.error("Registration error:", error);
    }
  };

  const nextStep = () => {
    // Validate current step before proceeding
    const currentStepData = watch();
    let canProceed = true;
    let errorMessage = "";

    switch (currentStep) {
      case 1: // Role Selection
        if (!currentStepData.corporateRole) {
          canProceed = false;
          errorMessage = "Please select a role before proceeding";
        }
        break;

      case 2: // Account Creation
        if (!currentStepData.email) {
          canProceed = false;
          errorMessage = "Please enter your email address";
        } else if (!otpVerified) {
          canProceed = false;
          errorMessage = "Please verify your email address with OTP";
        } else if (!currentStepData.lei) {
          canProceed = false;
          errorMessage = "Please enter your LEI (Legal Entity Identifier)";
        } else if (currentStepData.lei.length !== 20) {
          canProceed = false;
          errorMessage = "LEI must be exactly 20 characters";
        } else if (!currentStepData.password) {
          canProceed = false;
          errorMessage = "Please enter a password";
        } else if (currentStepData.password.length < 8) {
          canProceed = false;
          errorMessage = "Password must be at least 8 characters";
        } else if (
          !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/.test(
            currentStepData.password
          )
        ) {
          canProceed = false;
          errorMessage =
            "Password must contain uppercase, lowercase, numbers and special characters";
        } else if (!currentStepData.confirmPassword) {
          canProceed = false;
          errorMessage = "Please confirm your password";
        } else if (
          currentStepData.password !== currentStepData.confirmPassword
        ) {
          canProceed = false;
          errorMessage = "Passwords do not match";
        }
        break;

      case 3: // Bank Account Information
        if (bankAccountTypes.length === 0) {
          canProceed = false;
          errorMessage = "Please select at least one bank account type";
        } else {
          // Validate each selected account type
          for (const accountType of bankAccountTypes) {
            const account = bankAccounts[accountType];
            if (
              !account.bankName ||
              !account.accountName ||
              !account.accountNumber ||
              !account.accountCategory
            ) {
              canProceed = false;
              errorMessage = `Please complete all required fields for ${accountType} account`;
              break;
            }
            if (accountType === "islamic" && !account.shariahComplianceFile) {
              canProceed = false;
              errorMessage =
                "Shariah Compliance Declaration is required for Islamic accounts";
              break;
            }
          }
        }
        break;

      case 4: // Corporate Information
        if (!currentStepData.legalEntityName) {
          canProceed = false;
          errorMessage = "Please enter your Legal Entity Name";
        } else if (!currentStepData.businessRegistrationNumber) {
          canProceed = false;
          errorMessage = "Please enter your Business Registration Number";
        } else if (!currentStepData.registrationType) {
          canProceed = false;
          errorMessage = "Please select a Registration Type";
        }
        break;

      default:
        canProceed = true;
    }

    if (!canProceed) {
      setStepError(errorMessage);
      return;
    }

    // Clear any previous errors when proceeding
    setStepError("");

    setCurrentStep((prev) => Math.min(prev + 1, 4));
  };

  const isCurrentStepValid = () => {
    const currentStepData = watch();

    switch (currentStep) {
      case 1: // Role Selection
        return !!currentStepData.corporateRole;

      case 2: // Account Creation
        return (
          !!currentStepData.email &&
          otpVerified &&
          !!currentStepData.lei &&
          currentStepData.lei.length === 20 &&
          !!currentStepData.password &&
          currentStepData.password.length >= 8 &&
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/.test(
            currentStepData.password
          ) &&
          !!currentStepData.confirmPassword &&
          currentStepData.password === currentStepData.confirmPassword
        );

      case 3: // Bank Account Information
        if (bankAccountTypes.length === 0) return false;
        return bankAccountTypes.every((accountType) => {
          const account = bankAccounts[accountType];
          const hasRequiredFields =
            account.bankName &&
            account.accountName &&
            account.accountNumber &&
            account.accountCategory;
          const hasShariahFile =
            accountType !== "islamic" || account.shariahComplianceFile;
          return hasRequiredFields && hasShariahFile;
        });

      case 4: // Corporate Information
        return (
          !!currentStepData.legalEntityName &&
          !!currentStepData.businessRegistrationNumber &&
          !!currentStepData.registrationType &&
          !!currentStepData.acceptTerms &&
          !!currentStepData.acceptPrivacy
        );

      default:
        return true;
    }
  };

  const prevStep = () => {
    setStepError(""); // Clear errors when going back
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-start justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;

            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center w-20">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${isCompleted
                        ? "bg-primary-500 border-primary-500 text-white"
                        : isActive
                          ? "border-primary-500 text-primary-500"
                          : "border-gray-300 text-gray-400"
                      }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span
                    className={`text-xs mt-2 text-center ${isActive
                        ? "text-primary-600 font-medium"
                        : "text-gray-500"
                      }`}
                  >
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 ${isCompleted ? "bg-primary-500" : "bg-gray-200"
                      }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1: Role Selection */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="mb-6 text-center">
              <h3 className="text-xl font-semibold text-gray-900">
                Role Selection
              </h3>
              <p className="text-gray-600">
                Select your role type on the platform
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {corporateRoles.map((role) => (
                <label
                  key={role.value}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${watch("corporateRole") === role.value
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-primary-300"
                    }`}
                >
                  <input
                    type="radio"
                    value={role.value}
                    className="sr-only"
                    {...register("corporateRole", {
                      required: "Please select a role",
                    })}
                  />
                  <div className="flex items-start space-x-3">
                    <div
                      className={`w-4 h-4 rounded-full border-2 mt-1 ${watch("corporateRole") === role.value
                          ? "border-primary-500 bg-primary-500"
                          : "border-gray-300"
                        }`}
                    >
                      {watch("corporateRole") === role.value && (
                        <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {role.label}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {role.description}
                      </p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {errors.corporateRole && (
              <p className="form-error">{errors.corporateRole.message}</p>
            )}
          </div>
        )}

        {/* Step 2: Account Creation */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="mb-6 text-center">
              <h3 className="text-xl font-semibold text-gray-900">
                Account Creation
              </h3>
              <p className="text-gray-600">
                Set up your corporate account login credentials
              </p>
            </div>

            <div>
              <label className="form-label">Corporate Email Address *</label>
              <div className="flex gap-3">
                <input
                  type="email"
                  className={`form-input flex-1 ${errors.email ? "form-input-error" : ""
                    } ${otpVerified ? "border-green-500 bg-green-50" : ""}`}
                  placeholder="Enter corporate email address"
                  disabled={otpVerified}
                  {...register("email", {
                    required: "Email address is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Please enter a valid email address",
                    },
                  })}
                />
                <button
                  type="button"
                  onClick={sendOTP}
                  disabled={
                    otpLoading ||
                    !watch("email") ||
                    otpVerified ||
                    !canResendOtp
                  }
                  className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${otpVerified
                      ? "bg-green-100 text-green-800 border-green-300 cursor-not-allowed"
                      : !canResendOtp
                        ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                    }`}
                >
                  {otpLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                      Sending...
                    </div>
                  ) : otpVerified ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Verified
                    </div>
                  ) : !canResendOtp ? (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Resend ({otpCountdown}s)
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {otpSent ? "Resend OTP" : "Send OTP"}
                    </div>
                  )}
                </button>
              </div>
              {errors.email && (
                <p className="form-error">{errors.email.message}</p>
              )}

              {/* OTP Verification Section */}
              {otpSent && !otpVerified && (
                <div className="p-4 mt-4 border border-blue-200 rounded-lg bg-blue-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <p className="text-sm font-medium text-blue-900">
                      Email Verification Required
                    </p>
                  </div>
                  <p className="mb-3 text-sm text-blue-800">
                    We've sent a 6-digit verification code to your email
                    address. Please check your inbox and enter the code below.
                  </p>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={otpCode}
                      onChange={(e) =>
                        setOtpCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6)
                        )
                      }
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      className="flex-1 font-mono text-lg tracking-widest text-center form-input"
                    />
                    <button
                      type="button"
                      onClick={verifyOTP}
                      disabled={otpLoading || otpCode.length !== 6}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${otpLoading || otpCode.length !== 6
                          ? "bg-gray-100 text-gray-400 border border-gray-300 cursor-not-allowed"
                          : "bg-green-600 text-white border border-green-600 hover:bg-green-700"
                        }`}
                    >
                      {otpLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                          Verifying...
                        </div>
                      ) : (
                        "Verify"
                      )}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-blue-600">
                    Didn't receive the code? Check your spam folder or click
                    "Resend OTP" above.
                  </p>
                </div>
              )}

              {/* Success Message */}
              {otpVerified && (
                <div className="p-4 mt-4 border border-green-200 rounded-lg bg-green-50">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-sm font-medium text-green-900">
                      Email Verified Successfully!
                    </p>
                  </div>
                  <p className="mt-1 text-sm text-green-800">
                    Your email address has been verified. You can now proceed to
                    the next step.
                  </p>
                </div>
              )}

              {!otpSent && (
                <p className="mt-1 text-xs text-gray-500">
                  Click "Send OTP" to verify your email address before
                  proceeding
                </p>
              )}
            </div>

            <div>
              <label className="form-label">
                LEI (Legal Entity Identifier) *
              </label>
              <input
                type="text"
                className={`form-input ${errors.lei ? "form-input-error" : ""}`}
                placeholder="Enter LEI (e.g., 529900T8BM49AURSDO55)"
                maxLength={20}
                {...register("lei", {
                  required: "LEI (Legal Entity Identifier) is required",
                  minLength: {
                    value: 20,
                    message: "LEI must be exactly 20 characters",
                  },
                  maxLength: {
                    value: 20,
                    message: "LEI must be exactly 20 characters",
                  },
                  pattern: {
                    value: /^[A-Z0-9]{20}$/i,
                    message: "LEI must contain only letters and numbers",
                  },
                })}
                onChange={(e) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
              />
              {errors.lei && <p className="form-error">{errors.lei.message}</p>}
              <p className="mt-1 text-xs text-gray-500">
                A 20-character alphanumeric code that uniquely identifies legal
                entities globally
              </p>
            </div>

            <div>
              <label className="form-label">Password *</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`form-input pr-10 ${errors.password ? "form-input-error" : ""
                    }`}
                  placeholder="Enter password"
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters",
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
                      message:
                        "Password must contain uppercase, lowercase, numbers and special characters (!@#$%^&*)",
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="form-error">{errors.password.message}</p>
              )}

              {/* Password Requirements */}
              <div className="mt-2 text-sm">
                <p className="mb-2 text-gray-600">Password Requirements:</p>
                <div className="grid grid-cols-1 gap-1 text-xs md:grid-cols-2">
                  <div
                    className={`flex items-center space-x-2 ${watch("password")?.length >= 8
                        ? "text-green-600"
                        : "text-gray-500"
                      }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full ${watch("password")?.length >= 8
                          ? "bg-green-500"
                          : "bg-gray-300"
                        }`}
                    ></div>
                    <span>Minimum 8 characters</span>
                  </div>
                  <div
                    className={`flex items-center space-x-2 ${/[A-Z]/.test(watch("password") || "")
                        ? "text-green-600"
                        : "text-gray-500"
                      }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full ${/[A-Z]/.test(watch("password") || "")
                          ? "bg-green-500"
                          : "bg-gray-300"
                        }`}
                    ></div>
                    <span>Uppercase letter (A-Z)</span>
                  </div>
                  <div
                    className={`flex items-center space-x-2 ${/[a-z]/.test(watch("password") || "")
                        ? "text-green-600"
                        : "text-gray-500"
                      }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full ${/[a-z]/.test(watch("password") || "")
                          ? "bg-green-500"
                          : "bg-gray-300"
                        }`}
                    ></div>
                    <span>Lowercase letter (a-z)</span>
                  </div>
                  <div
                    className={`flex items-center space-x-2 ${/\d/.test(watch("password") || "")
                        ? "text-green-600"
                        : "text-gray-500"
                      }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full ${/\d/.test(watch("password") || "")
                          ? "bg-green-500"
                          : "bg-gray-300"
                        }`}
                    ></div>
                    <span>Number (0-9)</span>
                  </div>
                  <div
                    className={`flex items-center space-x-2 ${/[!@#$%^&*]/.test(watch("password") || "")
                        ? "text-green-600"
                        : "text-gray-500"
                      }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full ${/[!@#$%^&*]/.test(watch("password") || "")
                          ? "bg-green-500"
                          : "bg-gray-300"
                        }`}
                    ></div>
                    <span>Special character (!@#$%^&*)</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="form-label">Confirm Password *</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className={`form-input pr-10 ${errors.confirmPassword ? "form-input-error" : ""
                    }`}
                  placeholder="Re-enter password"
                  {...register("confirmPassword", {
                    required: "Please confirm password",
                    validate: (value) => {
                      if (!value) return "Please confirm password";
                      if (value !== watch("password"))
                        return "Passwords do not match";
                      return true;
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="form-error">{errors.confirmPassword.message}</p>
              )}

              {/* Password Confirmation Status */}
              {watch("confirmPassword") && (
                <div className="mt-2 text-sm">
                  <div
                    className={`flex items-center space-x-2 ${watch("confirmPassword") === watch("password")
                        ? "text-green-600"
                        : "text-red-600"
                      }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full ${watch("confirmPassword") === watch("password")
                          ? "bg-green-500"
                          : "bg-red-500"
                        }`}
                    ></div>
                    <span>
                      {watch("confirmPassword") === watch("password")
                        ? "Passwords match ✓"
                        : "Passwords do not match ✗"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Bank Account Information */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="mb-6 text-center">
              <h3 className="text-xl font-semibold text-gray-900">
                Bank Account Information
              </h3>
              <p className="text-gray-600">
                Select your bank account types and provide account details
              </p>
            </div>

            {/* Bank Account Type Selection */}
            <div className="space-y-4">
              <label className="form-label">Bank Account Type *</label>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${bankAccountTypes.includes("conventional")
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-primary-300"
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={bankAccountTypes.includes("conventional")}
                    onChange={(e) =>
                      handleBankAccountTypeChange(
                        "conventional",
                        e.target.checked
                      )
                    }
                    className="sr-only"
                  />
                  <div className="flex items-start space-x-3">
                    <div
                      className={`w-4 h-4 rounded border-2 mt-1 ${bankAccountTypes.includes("conventional")
                          ? "border-primary-500 bg-primary-500"
                          : "border-gray-300"
                        }`}
                    >
                      {bankAccountTypes.includes("conventional") && (
                        <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        Conventional Account
                      </h4>
                      <p className="text-sm text-gray-600">
                        Traditional banking account
                      </p>
                    </div>
                  </div>
                </label>

                <label
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${bankAccountTypes.includes("islamic")
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-primary-300"
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={bankAccountTypes.includes("islamic")}
                    onChange={(e) =>
                      handleBankAccountTypeChange("islamic", e.target.checked)
                    }
                    className="sr-only"
                  />
                  <div className="flex items-start space-x-3">
                    <div
                      className={`w-4 h-4 rounded border-2 mt-1 ${bankAccountTypes.includes("islamic")
                          ? "border-primary-500 bg-primary-500"
                          : "border-gray-300"
                        }`}
                    >
                      {bankAccountTypes.includes("islamic") && (
                        <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        Islamic Account
                      </h4>
                      <p className="text-sm text-gray-600">
                        Shariah-compliant banking account
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Bank Account Details */}
            {bankAccountTypes.map((accountType) => (
              <div
                key={accountType}
                className="p-6 border border-gray-200 rounded-lg"
              >
                <h4 className="mb-4 font-medium text-gray-900 capitalize">
                  {accountType} Account Details
                </h4>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="form-label">Bank Name *</label>
                    <select
                      value={bankAccounts[accountType].bankName}
                      onChange={(e) =>
                        updateBankAccount(
                          accountType,
                          "bankName",
                          e.target.value
                        )
                      }
                      className="form-input"
                    >
                      <option value="">Select bank</option>
                      {bankOptions.map((bank) => (
                        <option key={bank} value={bank}>
                          {bank}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Account Name *</label>
                    <input
                      type="text"
                      value={bankAccounts[accountType].accountName}
                      onChange={(e) =>
                        updateBankAccount(
                          accountType,
                          "accountName",
                          e.target.value
                        )
                      }
                      className="form-input"
                      placeholder="Enter account holder name"
                    />
                  </div>

                  <div>
                    <label className="form-label">Account Number *</label>
                    <input
                      type="text"
                      value={bankAccounts[accountType].accountNumber}
                      onChange={(e) =>
                        updateBankAccount(
                          accountType,
                          "accountNumber",
                          e.target.value
                        )
                      }
                      className="form-input"
                      placeholder="Enter account number"
                    />
                  </div>

                  <div>
                    <label className="form-label">Account Category *</label>
                    <select
                      value={bankAccounts[accountType].accountCategory}
                      onChange={(e) =>
                        updateBankAccount(
                          accountType,
                          "accountCategory",
                          e.target.value
                        )
                      }
                      className="form-input"
                    >
                      <option value="">Select category</option>
                      <option value="current">Current</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">SWIFT / BIC (Optional)</label>
                    <input
                      type="text"
                      value={bankAccounts[accountType].swiftBic}
                      onChange={(e) =>
                        updateBankAccount(
                          accountType,
                          "swiftBic",
                          e.target.value
                        )
                      }
                      className="form-input"
                      placeholder="Enter SWIFT/BIC code"
                    />
                  </div>

                  <div>
                    <label className="form-label">Branch Code (Optional)</label>
                    <input
                      type="text"
                      value={bankAccounts[accountType].branchCode}
                      onChange={(e) =>
                        updateBankAccount(
                          accountType,
                          "branchCode",
                          e.target.value
                        )
                      }
                      className="form-input"
                      placeholder="Enter branch code"
                    />
                  </div>
                </div>

                {/* Shariah Compliance Declaration for Islamic accounts */}
                {accountType === "islamic" && (
                  <div className="mt-4">
                    <label className="form-label">
                      Shariah Compliance Declaration *
                    </label>
                    <div className="p-4 text-center transition-colors border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-500">
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        id={`islamic_shariahCompliance`}
                        onChange={handleShariahComplianceUpload}
                      />
                      <label
                        htmlFor={`islamic_shariahCompliance`}
                        className="cursor-pointer"
                      >
                        <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">
                          {bankAccounts.islamic.shariahComplianceFile
                            ? bankAccounts.islamic.shariahComplianceFile.name
                            : "Upload Shariah Compliance Declaration"}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Supports PDF (max 10MB)
                        </p>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step 4: Corporate Information */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="mb-6 text-center">
              <h3 className="text-xl font-semibold text-gray-900">
                Corporate Information
              </h3>
              <p className="text-gray-600">
                Please provide your business details
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="form-label">Legal Entity Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter complete registered company name"
                  {...register("legalEntityName")}
                />
              </div>

              <div>
                <label className="form-label">
                  Business Registration Number *
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter SSM registration number"
                  {...register("businessRegistrationNumber")}
                />
              </div>

              <div>
                <label className="form-label">Registration Type *</label>
                <select
                  className="form-input"
                  {...register("registrationType")}
                >
                  <option value="">Select registration type</option>
                  <option value="Sdn Bhd">Sdn Bhd</option>
                  <option value="Bhd">Bhd</option>
                  <option value="LLP">LLP</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Sole Proprietorship">
                    Sole Proprietorship
                  </option>
                </select>
              </div>

              <div>
                <label className="form-label">Date of Incorporation</label>
                <input
                  type="date"
                  className="form-input"
                  {...register("dateOfIncorporation")}
                />
              </div>

              <div className="md:col-span-2">
                <label className="form-label">Registered Address</label>
                <textarea
                  className="form-input"
                  rows="3"
                  placeholder="Enter SSM registered address"
                  {...register("registeredAddress")}
                />
              </div>

              <div className="md:col-span-2">
                <label className="form-label">Operating Address</label>
                <textarea
                  className="form-input"
                  rows="3"
                  placeholder="Enter actual business operating address (if different from registered address)"
                  {...register("operatingAddress")}
                />
              </div>

              <div>
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  className={`form-input ${errors.phoneNumber ? "form-input-error" : ""
                    }`}
                  placeholder="Enter company phone number"
                  {...register("phoneNumber")}
                />
                {errors.phoneNumber && (
                  <p className="form-error">{errors.phoneNumber.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Website</label>
                <input
                  type="url"
                  className="form-input"
                  placeholder="Enter company website (optional)"
                  {...register("website")}
                />
              </div>

              <div>
                <label className="form-label">Business Nature</label>
                <select
                  className={`form-input ${errors.businessNature ? "form-input-error" : ""
                    }`}
                  {...register("businessNature")}
                >
                  <option value="">Select business nature</option>
                  <option value="Financial Services">Financial Services</option>
                  <option value="Technology">Technology</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Education">Education</option>
                  <option value="Other">Other</option>
                </select>
                {errors.businessNature && (
                  <p className="form-error">{errors.businessNature.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Annual Revenue Range</label>
                <select
                  className={`form-input ${errors.annualRevenue ? "form-input-error" : ""
                    }`}
                  {...register("annualRevenue")}
                >
                  <option value="">Select annual revenue range</option>
                  <option value="Below 1M">Below RM 1,000,000</option>
                  <option value="1M-5M">RM 1,000,000 - 5,000,000</option>
                  <option value="5M-25M">RM 5,000,000 - 25,000,000</option>
                  <option value="25M-100M">RM 25,000,000 - 100,000,000</option>
                  <option value="Above 100M">Above RM 100,000,000</option>
                </select>
                {errors.annualRevenue && (
                  <p className="form-error">{errors.annualRevenue.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Number of Employees</label>
                <select
                  className={`form-input ${errors.numberOfEmployees ? "form-input-error" : ""
                    }`}
                  {...register("numberOfEmployees")}
                >
                  <option value="">Select number of employees</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="Above 500">Above 500 employees</option>
                </select>
                {errors.numberOfEmployees && (
                  <p className="form-error">
                    {errors.numberOfEmployees.message}
                  </p>
                )}
              </div>
            </div>

            {/* Terms and Privacy Checkboxes */}
            <div className="mt-8 space-y-4">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  {...register("acceptTerms", {
                    required: "Must agree to Terms and Conditions",
                  })}
                />
                <span className="text-sm text-gray-700">
                  I have read and agree to the{" "}
                  <button
                    type="button"
                    className="text-primary-500 hover:underline"
                  >
                    Terms and Conditions
                  </button>
                </span>
              </label>
              {errors.acceptTerms && (
                <p className="form-error">{errors.acceptTerms.message}</p>
              )}

              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  {...register("acceptPrivacy", {
                    required: "Must agree to Privacy Policy",
                  })}
                />
                <span className="text-sm text-gray-700">
                  I have read and agree to the{" "}
                  <button
                    type="button"
                    className="text-primary-500 hover:underline"
                  >
                    Privacy Policy
                  </button>
                </span>
              </label>
              {errors.acceptPrivacy && (
                <p className="form-error">{errors.acceptPrivacy.message}</p>
              )}
            </div>
          </div>
        )}

        {/* Step Error Display */}
        {stepError && (
          <div className="p-4 mb-4 border border-red-200 rounded-lg bg-red-50">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="w-5 h-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{stepError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <div>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="btn-secondary"
              >
                Previous
              </button>
            )}
          </div>

          <div>
            {currentStep < 4 ? (
              <button
                type="button"
                onClick={nextStep}
                className={`btn-primary ${!isCurrentStepValid() ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                disabled={!isCurrentStepValid()}
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={isRegistering || !isCurrentStepValid()}
                className={`btn-primary flex items-center ${!isCurrentStepValid() ? "opacity-50 cursor-not-allowed" : ""
                  }`}
              >
                {isRegistering ? (
                  <LoadingSpinner size="sm" text="Registering..." />
                ) : (
                  "Complete Registration"
                )}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default KYBForm;
